import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CTAButton } from "@/components/CTAButton";
import { postPromoCheckout } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { Loader2, Lock, CalendarCheck, ShieldCheck } from "lucide-react";

const POSTE_OPTIONS = [...Array.from({ length: 15 }, (_, i) => `${i + 1}`), "Plus de 15"];

const EMPTY = { societe: "", siret: "", contact: "", email: "", telephone: "", postes: "" };

export const PromoBookingDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.societe || !form.siret || !form.contact || !form.email || !form.telephone || !form.postes) {
      toast.error("Merci de renseigner tous les champs pour continuer.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await postPromoCheckout(form);
      if (data?.url) {
        trackEvent("generate_lead", { method: "audit_payant", value: 120, currency: "EUR" });
        window.location.href = data.url; // redirection vers le paiement sécurisé Stripe
        return; // on ne remet pas loading à false : la page change
      }
      throw new Error("no-url");
    } catch (err) {
      const msg = err?.response?.status === 503
        ? "Le paiement en ligne n'est pas encore activé. Appelez-nous au 03 20 49 02 04."
        : "Une erreur est survenue. Réessayez, ou appelez-nous au 03 20 49 02 04.";
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) onOpenChange(v); }}>
      <DialogContent className="max-w-lg rounded-xl" data-testid="promo-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight text-navy">
            Réserver votre audit cybersécurité
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Renseignez votre entreprise, réglez en ligne, puis choisissez votre créneau. Les 100 € sont déduits si vous souscrivez ensuite une offre.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2" data-testid="promo-booking-form">
          <div className="space-y-1.5">
            <Label htmlFor="p-societe">Société *</Label>
            <Input id="p-societe" value={form.societe} onChange={set("societe")} placeholder="Nom de votre société" data-testid="promo-societe" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-siret">SIRET *</Label>
              <Input id="p-siret" value={form.siret} onChange={set("siret")} placeholder="14 chiffres" inputMode="numeric" data-testid="promo-siret" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-contact">Contact *</Label>
              <Input id="p-contact" value={form.contact} onChange={set("contact")} placeholder="Votre nom" data-testid="promo-contact" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-email">Email *</Label>
              <Input id="p-email" type="email" value={form.email} onChange={set("email")} placeholder="vous@entreprise.fr" data-testid="promo-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-tel">Téléphone *</Label>
              <Input id="p-tel" value={form.telephone} onChange={set("telephone")} placeholder="03 XX XX XX XX" data-testid="promo-telephone" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Nombre de postes *</Label>
            <Select value={form.postes} onValueChange={set("postes")}>
              <SelectTrigger data-testid="promo-postes"><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {POSTE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-[#f8fafc] border border-slate-200 p-3.5 flex items-center justify-between gap-3">
            <span className="text-sm text-slate-600">Audit cybersécurité</span>
            <span className="font-display text-lg font-bold text-navy tabular-nums">120 € TTC <span className="text-xs font-medium text-slate-400">(100 € HT)</span></span>
          </div>

          <CTAButton type="submit" size="lg" className="w-full" disabled={loading} data-testid="promo-pay-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4" /> Payer et choisir mon créneau</>}
          </CTAButton>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-amber-600" /> Paiement sécurisé Stripe</span>
            <span className="inline-flex items-center gap-1.5"><CalendarCheck className="w-3.5 h-3.5 text-amber-600" /> Créneau via Calendly ensuite</span>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
